var _classnames = $parcel$interopDefault(require("classnames"));

var {
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  useEffect
} = require("react");

function $parcel$interopDefault(a) {
  return a && a.__esModule ? a.default : a;
}

let $f09fcd7f5f367fc80aacfeac62ed2$var$map = new Map();
let $f09fcd7f5f367fc80aacfeac62ed2$var$id = 0; // don't want to conflict with ids from v2, this will guarantee something unique
// plus we'll know how many instances of this module are loaded on a page if there are more than one number ;)

let $f09fcd7f5f367fc80aacfeac62ed2$var$randomInstanceNumber = Math.round(Math.random() * 10000000000);

function useId(defaultId) {
  let [value, setValue] = useState(defaultId);
  let res = useMemo(() => value || "react-aria-" + $f09fcd7f5f367fc80aacfeac62ed2$var$randomInstanceNumber + "-" + ++$f09fcd7f5f367fc80aacfeac62ed2$var$id, [value]);
  $f09fcd7f5f367fc80aacfeac62ed2$var$map.set(res, setValue);
  return res;
}

exports.useId = useId;

function mergeIds(a, b) {
  if (a === b) {
    return a;
  }

  let setA = $f09fcd7f5f367fc80aacfeac62ed2$var$map.get(a);

  if (setA) {
    setA(b);
    return b;
  }

  let setB = $f09fcd7f5f367fc80aacfeac62ed2$var$map.get(b);

  if (setB) {
    setB(a);
    return a;
  }

  return b;
}

exports.mergeIds = mergeIds;

function useSlotId() {
  let [id, setId] = useState(useId());
  useLayoutEffect(() => {
    let setCurr = $f09fcd7f5f367fc80aacfeac62ed2$var$map.get(id);

    if (setCurr && !document.getElementById(id)) {
      setId(null);
    }
  }, [id]);
  return id;
}

exports.useSlotId = useSlotId;

/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
function chain() {
  for (var _len = arguments.length, callbacks = new Array(_len), _key = 0; _key < _len; _key++) {
    callbacks[_key] = arguments[_key];
  }

  return function () {
    for (let callback of callbacks) {
      if (typeof callback === 'function') {
        callback(...arguments);
      }
    }
  };
}

exports.chain = chain;

/**
 * Merges multiple props objects together. Event handlers are chained,
 * classNames are combined, and ids are deduplicated. For all other props,
 * b overrides a.
 * @param a - the first set of props to merge.
 * @param b - the second set of props to merge.
 */
function mergeProps(a, b) {
  let res = {};

  for (let key in a) {
    // Chain events
    if (/^on[A-Z]/.test(key) && typeof a[key] === 'function' && typeof b[key] === 'function') {
      res[key] = chain(a[key], b[key]); // Merge classnames, sometimes classNames are empty string which eval to false, so we just need to do a type check
    } else if (key === 'className' && typeof a.className === 'string' && typeof b.className === 'string') {
      res[key] = _classnames(a.className, b.className);
    } else if (key === 'UNSAFE_className' && typeof a.UNSAFE_className === 'string' && typeof b.UNSAFE_className === 'string') {
      res[key] = _classnames(a.UNSAFE_className, b.UNSAFE_className);
    } else if (key === 'id' && a.id && b.id) {
      res.id = mergeIds(a.id, b.id); // Override others
    } else {
      res[key] = b[key] !== undefined ? b[key] : a[key];
    }
  } // Add props from b that are not in a


  for (let key in b) {
    if (a[key] === undefined) {
      res[key] = b[key];
    }
  }

  return res;
}

exports.mergeProps = mergeProps;

function clamp(value, min, max) {
  if (min === void 0) {
    min = -Infinity;
  }

  if (max === void 0) {
    max = Infinity;
  }

  return Math.min(Math.max(value, min), max);
}

exports.clamp = clamp;

function getOffset(element, reverse, orientation) {
  if (orientation === void 0) {
    orientation = 'horizontal';
  }

  let rect = element.getBoundingClientRect();

  if (reverse) {
    return orientation === 'horizontal' ? rect.right : rect.bottom;
  }

  return orientation === 'horizontal' ? rect.left : rect.top;
}

exports.getOffset = getOffset;

// created for splitview, this should be reusable for things like sliders/dials
// It also handles keyboard events on the target allowing for increment/decrement by a given stepsize as well as minifying/maximizing and toggling between minified and previous size
// It can also take a 'reverse' param to say if we should measure from the right/bottom instead of the top/left
// It can also handle either a vertical or horizontal movement, but not both at the same time
function useDrag1D(props) {
  let {
    containerRef,
    reverse,
    orientation,
    onHover,
    onDrag,
    onPositionChange,
    onIncrement,
    onDecrement,
    onIncrementToMax,
    onDecrementToMin,
    onCollapseToggle
  } = props;

  let getPosition = e => orientation === 'horizontal' ? e.clientX : e.clientY;

  let getNextOffset = e => {
    let containerOffset = getOffset(containerRef.current, reverse, orientation);
    let mouseOffset = getPosition(e);
    let nextOffset = reverse ? containerOffset - mouseOffset : mouseOffset - containerOffset;
    return nextOffset;
  };

  let dragging = useRef(false);
  let prevPosition = useRef(0);

  let onMouseDragged = e => {
    e.preventDefault();
    let nextOffset = getNextOffset(e);

    if (!dragging.current) {
      dragging.current = true;

      if (onDrag) {
        onDrag(true);
      }

      if (onPositionChange) {
        onPositionChange(nextOffset);
      }
    }

    if (prevPosition.current === nextOffset) {
      return;
    }

    prevPosition.current = nextOffset;

    if (onPositionChange) {
      onPositionChange(nextOffset);
    }
  };

  let onMouseUp = e => {
    dragging.current = false;
    let nextOffset = getNextOffset(e);

    if (onDrag) {
      onDrag(false);
    }

    if (onPositionChange) {
      onPositionChange(nextOffset);
    }

    window.removeEventListener('mouseup', onMouseUp, false);
    window.removeEventListener('mousemove', onMouseDragged, false);
  };

  let onMouseDown = () => {
    window.addEventListener('mousemove', onMouseDragged, false);
    window.addEventListener('mouseup', onMouseUp, false);
  };

  let onMouseEnter = () => {
    if (onHover) {
      onHover(true);
    }
  };

  let onMouseOut = () => {
    if (onHover) {
      onHover(false);
    }
  };

  let onKeyDown = e => {
    e.preventDefault();

    switch (e.key) {
      case 'Left':
      case 'ArrowLeft':
        if (orientation === 'horizontal') {
          if (onDecrement && !reverse) {
            onDecrement();
          } else if (onIncrement && reverse) {
            onIncrement();
          }
        }

        break;

      case 'Up':
      case 'ArrowUp':
        if (orientation === 'vertical') {
          if (onDecrement && !reverse) {
            onDecrement();
          } else if (onIncrement && reverse) {
            onIncrement();
          }
        }

        break;

      case 'Right':
      case 'ArrowRight':
        if (orientation === 'horizontal') {
          if (onIncrement && !reverse) {
            onIncrement();
          } else if (onDecrement && reverse) {
            onDecrement();
          }
        }

        break;

      case 'Down':
      case 'ArrowDown':
        if (orientation === 'vertical') {
          if (onIncrement && !reverse) {
            onIncrement();
          } else if (onDecrement && reverse) {
            onDecrement();
          }
        }

        break;

      case 'Home':
        if (onDecrementToMin) {
          onDecrementToMin();
        }

        break;

      case 'End':
        if (onIncrementToMax) {
          onIncrementToMax();
        }

        break;

      case 'Enter':
        if (onCollapseToggle) {
          onCollapseToggle();
        }

        break;
    }
  };

  return {
    onMouseDown,
    onMouseEnter,
    onMouseOut,
    onKeyDown
  };
}

exports.useDrag1D = useDrag1D;

function useLabels(props, defaultLabel) {
  let {
    id,
    'aria-label': label,
    'aria-labelledby': labelledBy
  } = props; // If there is both an aria-label and aria-labelledby, 
  // combine them by pointing to the element itself.

  id = useId(id);

  if (labelledBy && label) {
    let ids = new Set([...labelledBy.trim().split(/\s+/), id]);
    labelledBy = [...ids].join(' ');
  } else if (labelledBy) {
    labelledBy = labelledBy.trim().split(/\s+/).join(' ');
  } // If no labels are provided, use the default


  if (!label && !labelledBy && defaultLabel) {
    label = defaultLabel;
  }

  return {
    id,
    'aria-label': label,
    'aria-labelledby': labelledBy
  };
}

exports.useLabels = useLabels;

// Like useEffect, but only called for updates after the initial render.
function useUpdateEffect(effect, dependencies) {
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      effect();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, dependencies);
}

exports.useUpdateEffect = useUpdateEffect;

// This is a polyfill for element.focus({preventScroll: true});
// Currently necessary for Safari and old Edge:
// https://caniuse.com/#feat=mdn-api_htmlelement_focus_preventscroll_option
// See https://bugs.webkit.org/show_bug.cgi?id=178583
//
// Original licensing for the following methods can be found in the
// NOTICE file in the root directory of this source tree.
// See https://github.com/calvellido/focus-options-polyfill
function focusWithoutScrolling(element) {
  if ($d2a175ba498dd7834499f256dfb330bc$var$supportsPreventScroll()) {
    element.focus({
      preventScroll: true
    });
  } else {
    let scrollableElements = $d2a175ba498dd7834499f256dfb330bc$var$getScrollableElements(element);
    element.focus();
    $d2a175ba498dd7834499f256dfb330bc$var$restoreScrollPosition(scrollableElements);
  }
}

exports.focusWithoutScrolling = focusWithoutScrolling;
let $d2a175ba498dd7834499f256dfb330bc$var$supportsPreventScrollCached = null;

function $d2a175ba498dd7834499f256dfb330bc$var$supportsPreventScroll() {
  if ($d2a175ba498dd7834499f256dfb330bc$var$supportsPreventScrollCached == null) {
    $d2a175ba498dd7834499f256dfb330bc$var$supportsPreventScrollCached = false;

    try {
      var focusElem = document.createElement('div');
      focusElem.focus({
        get preventScroll() {
          $d2a175ba498dd7834499f256dfb330bc$var$supportsPreventScrollCached = true;
          return true;
        }

      });
    } catch (e) {// Ignore
    }
  }

  return $d2a175ba498dd7834499f256dfb330bc$var$supportsPreventScrollCached;
}

function $d2a175ba498dd7834499f256dfb330bc$var$getScrollableElements(element) {
  var parent = element.parentNode;
  var scrollableElements = [];
  var rootScrollingElement = document.scrollingElement || document.documentElement;

  while (parent instanceof HTMLElement && parent !== rootScrollingElement) {
    if (parent.offsetHeight < parent.scrollHeight || parent.offsetWidth < parent.scrollWidth) {
      scrollableElements.push({
        element: parent,
        scrollTop: parent.scrollTop,
        scrollLeft: parent.scrollLeft
      });
    }

    parent = parent.parentNode;
  }

  if (rootScrollingElement instanceof HTMLElement) {
    scrollableElements.push({
      element: rootScrollingElement,
      scrollTop: rootScrollingElement.scrollTop,
      scrollLeft: rootScrollingElement.scrollLeft
    });
  }

  return scrollableElements;
}

function $d2a175ba498dd7834499f256dfb330bc$var$restoreScrollPosition(scrollableElements) {
  for (let {
    element,
    scrollTop,
    scrollLeft
  } of scrollableElements) {
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  }
}

const $a736ffc3e05a0bfc1508098ba395b41$var$DOMPropNames = new Set(['id']);
const $a736ffc3e05a0bfc1508098ba395b41$var$labelablePropNames = new Set(['aria-label', 'aria-labelledby', 'aria-describedby', 'aria-details']);
const $a736ffc3e05a0bfc1508098ba395b41$var$propRe = /^(data-.*)$/; // Filters out all props that aren't valid DOM props or are user defined via override prop obj.

function filterDOMProps(props, opts) {
  if (opts === void 0) {
    opts = {};
  }

  let {
    labelable,
    propNames
  } = opts;
  let filteredProps = {};

  for (const prop in props) {
    if (Object.prototype.hasOwnProperty.call(props, prop) && ($a736ffc3e05a0bfc1508098ba395b41$var$DOMPropNames.has(prop) || labelable && $a736ffc3e05a0bfc1508098ba395b41$var$labelablePropNames.has(prop) || (propNames == null ? void 0 : propNames.has(prop)) || $a736ffc3e05a0bfc1508098ba395b41$var$propRe.test(prop))) {
      filteredProps[prop] = props[prop];
    }
  }

  return filteredProps;
}

exports.filterDOMProps = filterDOMProps;
//# sourceMappingURL=main.js.map
